import { XMarkIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { CanvasIssue } from './DefinitionCanvas';
import {
  ArrowRightStartOnRectangleIcon,
  CpuChipIcon,
  CommandLineIcon,
  ArrowsPointingOutIcon,
  ArrowLongRightIcon,
} from '@heroicons/react/24/outline';
import {
  CollaborationNode,
  CollaborationEdge,
  isAgentNode,
  isFunctionNode,
  isBranchNode,
  isEntryNode,
  AgentNode,
  FunctionNode,
  BranchNode,
  EntryNode,
} from '../../types/definition';
import {
  EntryNodeProperties,
  AgentNodeProperties,
  FunctionNodeProperties,
  BranchNodeProperties,
  EdgeProperties,
} from './properties';

type Selection =
  | { type: 'node'; node: CollaborationNode }
  | { type: 'edge'; edge: CollaborationEdge; sourceNodeName?: string; targetNodeName?: string };

interface PropertiesPanelProps {
  selection: Selection | null;
  onClose: () => void;
  onCollapse?: () => void;
  onNodeChange: (nodeId: string, updates: Partial<CollaborationNode>) => void;
  onEdgeChange: (edgeId: string, updates: Partial<CollaborationEdge>) => void;
  /** Problems affecting the selected node, client- and server-side (task 774). */
  issues?: CanvasIssue[];
  /**
   * Inspect, do not edit (task 881).
   *
   * The read-only canvas opens this same panel on a click, because reading what
   * a node is wired to is the commonest reason to open a graph. It travels down
   * to every editor below, which render their same controls with `disabled` and
   * with no change handler attached — so the wiring reads exactly as it does in
   * edit mode and nothing about looking at it can write.
   */
  readOnly?: boolean;
}

export function PropertiesPanel({
  selection,
  onClose,
  onCollapse,
  onNodeChange,
  onEdgeChange,
  issues,
  readOnly = false,
}: PropertiesPanelProps) {
  if (!selection) return null;

  const getHeader = () => {
    if (selection.type === 'edge') {
      return {
        icon: ArrowLongRightIcon,
        title: 'Edge',
        color: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-100 dark:bg-gray-800',
      };
    }

    const node = selection.node;
    if (isEntryNode(node)) {
      return {
        icon: ArrowRightStartOnRectangleIcon,
        title: 'Entry Node',
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900',
      };
    }
    if (isAgentNode(node)) {
      return {
        icon: CpuChipIcon,
        title: 'Agent Node',
        color: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-100 dark:bg-blue-900',
      };
    }
    if (isFunctionNode(node)) {
      return {
        icon: CommandLineIcon,
        title: 'Function Node',
        color: 'text-purple-600 dark:text-purple-400',
        bgColor: 'bg-purple-100 dark:bg-purple-900',
      };
    }
    if (isBranchNode(node)) {
      return {
        icon: ArrowsPointingOutIcon,
        title: 'Branch Node',
        color: 'text-orange-600 dark:text-orange-400',
        bgColor: 'bg-orange-100 dark:bg-orange-900',
      };
    }

    return {
      icon: CpuChipIcon,
      title: 'Node',
      color: 'text-gray-600 dark:text-gray-400',
      bgColor: 'bg-gray-100 dark:bg-gray-800',
    };
  };

  const header = getHeader();
  const Icon = header.icon;

  const renderProperties = () => {
    if (selection.type === 'edge') {
      return (
        <EdgeProperties
          edge={selection.edge}
          sourceNodeName={selection.sourceNodeName}
          targetNodeName={selection.targetNodeName}
          readOnly={readOnly}
          onChange={(updates) => onEdgeChange(selection.edge.id || '', updates)}
        />
      );
    }

    const node = selection.node;
    const handleChange = (updates: Partial<CollaborationNode>) => {
      onNodeChange(node.id, updates);
    };

    if (isEntryNode(node)) {
      return <EntryNodeProperties node={node as EntryNode} readOnly={readOnly} onChange={handleChange} />;
    }
    if (isAgentNode(node)) {
      return <AgentNodeProperties node={node as AgentNode} readOnly={readOnly} onChange={handleChange} />;
    }
    if (isFunctionNode(node)) {
      return <FunctionNodeProperties node={node as FunctionNode} readOnly={readOnly} onChange={handleChange} />;
    }
    if (isBranchNode(node)) {
      return <BranchNodeProperties node={node as BranchNode} readOnly={readOnly} onChange={handleChange} />;
    }

    return <div className="text-gray-500">Unknown node type</div>;
  };

  return (
    <div
      data-testid="properties-panel"
      data-readonly={readOnly ? 'true' : 'false'}
      aria-readonly={readOnly || undefined}
      className="w-80 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col h-full"
    >
      {/* Header. Close and Collapse are the only controls on it, and neither
          writes, so both survive read-only (task 881) — a panel a reader cannot
          dismiss is worse than no panel. */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-md ${header.bgColor}`}>
              <Icon className={`w-4 h-4 ${header.color}`} />
            </div>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {header.title}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {onCollapse && (
              <button
                onClick={onCollapse}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
                title="Collapse panel"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
              title="Close panel"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
        {/* Why the fields below are inert, in the canvas footer's own words —
            without it a panel full of greyed-out controls reads as broken. */}
        {readOnly && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Read-only. Choose Edit to change the graph.
          </p>
        )}
      </div>

      {/* Properties Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* The selected node's problems, above its fields (task 774) — so the
            message and the field that fixes it are on the same screen. Sits
            outside `renderProperties()` so every node type gets it without any
            of the per-type editors having to know about validation. */}
        {issues && issues.length > 0 && (
          <ul data-testid="properties-issues" className="mb-4 space-y-2">
            {issues.map((issue, index) => (
              <li
                key={`${issue.source}-${issue.field}-${index}`}
                data-testid="properties-issue"
                className={
                  issue.severity === 'error'
                    ? 'rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300'
                    : 'rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300'
                }
              >
                <span className="font-mono font-medium">{issue.field}</span>
                <span className="ml-1.5 rounded-full bg-white/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wide dark:bg-black/20">
                  {issue.source}
                </span>
                <span className="mt-1 block">{issue.message}</span>
              </li>
            ))}
          </ul>
        )}
        {renderProperties()}
      </div>

      {/* Footer with ID */}
      {selection.type === 'node' && (
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-400 dark:text-gray-500">
            <span className="font-medium">ID:</span>{' '}
            <span className="font-mono">{selection.node.id}</span>
          </div>
        </div>
      )}
    </div>
  );
}
